import React, { useCallback, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import { useDevice } from "hooks";

interface EmptyLookThroughProps {
  onFileUpload: (preselectedFileList?: FileList) => void;
}

// Mocked data for the empty state visualization
const mockedChartData = [
  { name: "Technology", y: 44, color: "#071F35" },
  { name: "Energy", y: 38, color: "#496682" },
  { name: "Healthcare", y: 15, color: "#C09560" },
  { name: "Real Estate", y: 3, color: "#26A6AE" }
];

export const EmptyLookThrough: React.FC<EmptyLookThroughProps> = ({ onFileUpload }) => {
  const { isMobile } = useDevice();
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container, not a child element
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const allFiles = e.dataTransfer.files;
      if (allFiles.length > 0) {
        onFileUpload(allFiles);
      }
    },
    [onFileUpload]
  );

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 100,
      backgroundColor: "transparent",
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },
    title: {
      text: ""
    },
    tooltip: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      pie: {
        innerSize: "60%",
        size: 133,
        center: ["50%", "100%"],
        startAngle: -90,
        endAngle: 90,
        dataLabels: {
          enabled: true,
          distance: 5,
          connectorWidth: 0,
          style: {
            fontSize: "14px",
            fontWeight: "400",
            fontFamily: "Roboto",
            color: "#496682"
          },
          format: "{point.percentage:.0f}%"
        },
        showInLegend: false,
        borderWidth: 0
      }
    },
    series: [
      {
        type: "pie",
        data: mockedChartData
      }
    ],
    exporting: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    }
  };

  return (
    <Box
      sx={{
        bgcolor: isDragging ? "#E8F0F8" : "#F6F8FC",
        border: isDragging ? "2px dashed #7b92a9" : "1px solid #DBE8F5",
        borderRadius: "8px",
        minHeight: "186px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        p: isMobile ? "20px" : "10px",
        gap: isMobile ? "20px" : "10px",
        overflow: "hidden",
        transition: "all 0.2s ease-in-out"
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Pie Chart with Legend */}
      <Box
        sx={{
          display: "flex",
          gap: "10px",
          alignItems: "flex-start",
          justifyContent: "center",
          bgcolor: "white",
          borderRadius: "8px",
          padding: "16px 10px",
          maxHeight: "111px",
          flexShrink: 0,
          width: isMobile ? "100%" : "auto"
        }}
      >
        {/* Pie Chart */}
        <Box
          sx={{
            width: "215px",
            height: "100px",
            position: "relative",

            flexShrink: 0
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
        </Box>

        {/* Legend */}
        {!isMobile && (
          <Stack
            sx={{
              flexDirection: "column",
              gap: "1px",
              py: "12px",
              width: "163px",
              flexShrink: 0
            }}
          >
            {mockedChartData
              .filter((item) => item.name !== "Real Estate")
              .map((item) => (
                <Stack
                  key={item.name}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    width: "100%"
                  }}
                >
                  <Stack direction="row" alignItems="center" gap="5px">
                    <Box
                      sx={{
                        width: "9px",
                        height: "9px",
                        borderRadius: "50%",
                        bgcolor: item.color,
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#071f35"
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      color: "#071f35"
                    }}
                  >
                    {item.y}%
                  </Typography>
                </Stack>
              ))}
          </Stack>
        )}
      </Box>

      {/* Text and Upload Section */}
      <Stack
        sx={{
          alignItems: isMobile ? "center" : "flex-start",
          gap: "10px",
          maxWidth: isMobile ? "100%" : "438px",
          textAlign: isMobile ? "center" : "left",
          flex: 1,
          justifyContent: "center",
          px: isMobile ? 0 : "20px"
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "18px" : "20px",
            fontWeight: 400,
            lineHeight: isMobile ? "24px" : "28px",
            color: "#0a3142"
          }}
        >
          Analyze your portfolio with powerful tools
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          gap="10px"
          sx={{
            flexWrap: "wrap",
            justifyContent: isMobile ? "center" : "flex-start"
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: isMobile ? "16px" : "20px" }} />}
            onClick={() => onFileUpload()}
            sx={{
              borderColor: "#7b92a9",
              color: "#071f35",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 400,
              textTransform: "none",
              borderRadius: "32px",
              px: isMobile ? "12px" : "16px",
              py: "4px",
              minWidth: "auto",
              height: "auto",
              "&:hover": {
                borderColor: "#071f35",
                backgroundColor: "transparent"
              }
            }}
          >
            Upload Data
          </Button>
          {!isMobile && (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "16px",
                color: "#496682"
              }}
            >
              or drag and drop a file.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
